import React, { useState } from 'react';
import { X, Plus, Trash, AlertCircle } from 'lucide-react';
import { verticals, payoutModels, currencies, geos, devices, operatingSystems, browsers, languages, segments, algorithmTypes, pacingOptions, pixelTypes, aspectRatios } from '../data/mockData';
interface BackofferFormProps {
  mode: 'create' | 'edit';
  initialData?: any;
  onSubmit: (data: any) => void;
  onCancel: () => void;
}
export const BackofferForm: React.FC<BackofferFormProps> = ({
  mode,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [activeTab, setActiveTab] = useState('basics');
  const [formData, setFormData] = useState(initialData || {
    id: `bo_${Date.now()}`,
    status: 'active',
    name: '',
    partnerId: '',
    partnerName: '',
    verticalId: '',
    description: '',
    baseUrl: '',
    trackingParams: {
      utm_source: 'sinshift',
      utm_campaign: 'fallback',
      subid: '{subid}',
      click_id: '{click_id}'
    },
    payoutModelId: 'cpa',
    currencyId: 'usd',
    payoutAmount: 0,
    holdDays: 7,
    payoutNotes: '',
    targeting: {
      geos: [],
      excludedGeos: [],
      devices: [],
      operatingSystems: [],
      browsers: [],
      languages: [],
      timeOfDay: {
        start: 0,
        end: 24
      },
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6]
    },
    segmentTargeting: {
      segments: [],
      fallbackType: 'persona'
    },
    caps: {
      daily: 1000,
      hourly: 100,
      frequency: 3,
      pacingId: 'even'
    },
    priority: 10,
    weight: 100,
    compliance: {
      adultFlag: false,
      restrictedGeos: [],
      ageGate: false,
      kycNeeded: false,
      partnerTermsUrl: '',
      internalRiskScore: 1
    },
    tracking: {
      clickMacroTokens: '{click_id}',
      postbackUrl: 'https://sinshift.com/postback?click_id={click_id}&status={status}',
      webhookEnabled: true,
      pixelTypeId: 's2s'
    },
    creatives: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const tabs = [{
    id: 'basics',
    label: 'Offer Basics'
  }, {
    id: 'affiliate',
    label: 'Affiliate Link'
  }, {
    id: 'payout',
    label: 'Payout Model'
  }, {
    id: 'targeting',
    label: 'Targeting'
  }, {
    id: 'segment',
    label: 'Segment Targeting'
  }, {
    id: 'caps',
    label: 'Caps & Throttling'
  }, {
    id: 'priority',
    label: 'Priority & Weight'
  }, {
    id: 'compliance',
    label: 'Compliance & Risk'
  }, {
    id: 'tracking',
    label: 'Tracking & Postbacks'
  }, {
    id: 'creatives',
    label: 'Creatives'
  }, {
    id: 'qa',
    label: 'QA'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const isCheckbox = type === 'checkbox';
    const checked = (e.target as HTMLInputElement).checked;
    // Handle nested properties
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: isCheckbox ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: isCheckbox ? checked : value
      }));
    }
  };
  const handleTrackingParamChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      trackingParams: {
        ...prev.trackingParams,
        [key]: value
      }
    }));
  };
  const handleAddTrackingParam = () => {
    const newKey = prompt('Enter parameter name:');
    if (newKey && !formData.trackingParams[newKey]) {
      setFormData(prev => ({
        ...prev,
        trackingParams: {
          ...prev.trackingParams,
          [newKey]: ''
        }
      }));
    }
  };
  const handleRemoveTrackingParam = (key: string) => {
    setFormData(prev => {
      const newTrackingParams = {
        ...prev.trackingParams
      };
      delete newTrackingParams[key];
      return {
        ...prev,
        trackingParams: newTrackingParams
      };
    });
  };
  const handleMultiSelect = (field: string, value: string, isSelected: boolean) => {
    // Handle nested properties for arrays
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => {
        const currentArray = [...(prev[parent][child] || [])];
        if (isSelected && !currentArray.includes(value)) {
          currentArray.push(value);
        } else if (!isSelected && currentArray.includes(value)) {
          const index = currentArray.indexOf(value);
          currentArray.splice(index, 1);
        }
        return {
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: currentArray
          }
        };
      });
    } else {
      setFormData(prev => {
        const currentArray = [...(prev[field] || [])];
        if (isSelected && !currentArray.includes(value)) {
          currentArray.push(value);
        } else if (!isSelected && currentArray.includes(value)) {
          const index = currentArray.indexOf(value);
          currentArray.splice(index, 1);
        }
        return {
          ...prev,
          [field]: currentArray
        };
      });
    }
  };
  const handleAddCreative = () => {
    setFormData(prev => ({
      ...prev,
      creatives: [...prev.creatives, {
        id: `creative_${Date.now()}`,
        type: 'banner',
        aspectRatioId: '16_9',
        url: '',
        headline: '',
        cta: '',
        approved: false
      }]
    }));
  };
  const handleCreativeChange = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const creatives = [...prev.creatives];
      creatives[index] = {
        ...creatives[index],
        [field]: value
      };
      return {
        ...prev,
        creatives
      };
    });
  };
  const handleRemoveCreative = (index: number) => {
    setFormData(prev => {
      const creatives = [...prev.creatives];
      creatives.splice(index, 1);
      return {
        ...prev,
        creatives
      };
    });
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Validate basic fields
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.partnerName.trim()) newErrors.partnerName = 'Partner name is required';
    if (!formData.verticalId) newErrors.verticalId = 'Vertical is required';
    // Validate affiliate link
    if (!formData.baseUrl.trim()) {
      newErrors.baseUrl = 'Base URL is required';
    } else if (!formData.baseUrl.startsWith('http://') && !formData.baseUrl.startsWith('https://')) {
      newErrors.baseUrl = 'URL must start with http:// or https://';
    }
    // Validate payout
    if (formData.payoutAmount <= 0) {
      newErrors.payoutAmount = 'Payout amount must be greater than 0';
    }
    // Validate targeting
    if (formData.targeting.geos.length === 0) {
      newErrors['targeting.geos'] = 'At least one GEO is required';
    }
    if (formData.targeting.devices.length === 0) {
      newErrors['targeting.devices'] = 'At least one device is required';
    }
    // Validate segment targeting
    if (formData.segmentTargeting.segments.length === 0) {
      newErrors['segmentTargeting.segments'] = 'At least one segment is required';
    }
    // Validate caps
    if (formData.caps.daily <= 0) {
      newErrors['caps.daily'] = 'Daily cap must be greater than 0';
    }
    if (formData.caps.hourly <= 0) {
      newErrors['caps.hourly'] = 'Hourly cap must be greater than 0';
    }
    if (formData.caps.frequency <= 0) {
      newErrors['caps.frequency'] = 'Frequency cap must be greater than 0';
    }
    // Validate priority & weight
    if (formData.priority <= 0) {
      newErrors.priority = 'Priority must be greater than 0';
    }
    if (formData.weight <= 0) {
      newErrors.weight = 'Weight must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      // Find the first tab with an error and switch to it
      for (const tab of tabs) {
        const hasError = Object.keys(errors).some(key => {
          if (key.includes('.')) {
            const [parent] = key.split('.');
            return tab.id === parent;
          }
          return tab.id === key;
        });
        if (hasError) {
          setActiveTab(tab.id);
          break;
        }
      }
    }
  };
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basics':
        return <div className="space-y-6" data-id="element-12151">
            <div data-id="element-12152">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12153">
                Status
              </label>
              <select name="status" value={formData.status} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12154">
                <option value="active" data-id="element-12155">Active</option>
                <option value="inactive" data-id="element-12156">Inactive</option>
              </select>
            </div>
            <div data-id="element-12157">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12158">
                Offer Name*
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.name ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., Premium Cams Offer" data-id="element-12159" />
              {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12160">
                  {errors.name}
                </p>}
            </div>
            <div data-id="element-12161">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12162">
                Partner/Network Name*
              </label>
              <input type="text" name="partnerName" value={formData.partnerName} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.partnerName ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="e.g., StreamMate" data-id="element-12163" />
              {errors.partnerName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12164">
                  {errors.partnerName}
                </p>}
            </div>
            <div data-id="element-12165">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12166">
                Vertical*
              </label>
              <select name="verticalId" value={formData.verticalId} onChange={handleChange} className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border ${errors.verticalId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white`} data-id="element-12167">
                <option value="" data-id="element-12168">Select a vertical</option>
                {verticals.map(vertical => <option key={vertical.id} value={vertical.id} data-id="element-12169">
                    {vertical.name}
                  </option>)}
              </select>
              {errors.verticalId && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12170">
                  {errors.verticalId}
                </p>}
            </div>
            <div data-id="element-12171">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12172">
                Description
              </label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="Brief description of the offer" data-id="element-12173" />
            </div>
          </div>;
      case 'affiliate':
        return <div className="space-y-6" data-id="element-12174">
            <div data-id="element-12175">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12176">
                Base URL*
              </label>
              <input type="text" name="baseUrl" value={formData.baseUrl} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.baseUrl ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="https://example.com/offer" data-id="element-12177" />
              {errors.baseUrl && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12178">
                  {errors.baseUrl}
                </p>}
            </div>
            <div data-id="element-12179">
              <div className="flex justify-between items-center mb-2" data-id="element-12180">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-12181">
                  Tracking Parameters
                </label>
                <button type="button" onClick={handleAddTrackingParam} className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors" data-id="element-12182">
                  Add Parameter
                </button>
              </div>
              <div className="space-y-2 border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12183">
                {Object.entries(formData.trackingParams).map(([key, value]) => <div key={key} className="flex items-center gap-2" data-id="element-12184">
                    <div className="flex-1 flex items-center gap-2" data-id="element-12185">
                      <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[100px]" data-id="element-12186">
                        {key}:
                      </span>
                      <input type="text" value={value as string} onChange={e => handleTrackingParamChange(key, e.target.value)} className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-12187" />
                    </div>
                    <button type="button" onClick={() => handleRemoveTrackingParam(key)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" data-id="element-12188">
                      <X size={16} data-id="element-12189" />
                    </button>
                  </div>)}
                {Object.keys(formData.trackingParams).length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-12190">
                    No tracking parameters defined.
                  </p>}
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md" data-id="element-12191">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2" data-id="element-12192">
                Available Macros
              </h4>
              <div className="grid grid-cols-2 gap-2" data-id="element-12193">
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12194">
                  {'{click_id}'} - Unique click identifier
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12195">
                  {'{subid}'} - Sub-ID for tracking
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12196">
                  {'{geo}'} - User's GEO code
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12197">
                  {'{device}'} - User's device type
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12198">
                  {'{os}'} - User's operating system
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-400" data-id="element-12199">
                  {'{browser}'} - User's browser
                </div>
              </div>
            </div>
          </div>;
      case 'payout':
        return <div className="space-y-6" data-id="element-12200">
            <div data-id="element-12201">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12202">
                Payout Model*
              </label>
              <select name="payoutModelId" value={formData.payoutModelId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12203">
                {payoutModels.map(model => <option key={model.id} value={model.id} data-id="element-12204">
                    {model.name} ({model.description})
                  </option>)}
              </select>
            </div>
            <div data-id="element-12205">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12206">
                Currency
              </label>
              <select name="currencyId" value={formData.currencyId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12207">
                {currencies.map(currency => <option key={currency.id} value={currency.id} data-id="element-12208">
                    {currency.name} ({currency.symbol})
                  </option>)}
              </select>
            </div>
            <div data-id="element-12209">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12210">
                Payout Amount*
              </label>
              <div className="mt-1 relative rounded-md shadow-sm" data-id="element-12211">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12212">
                  <span className="text-gray-500 sm:text-sm" data-id="element-12213">
                    {currencies.find(c => c.id === formData.currencyId)?.symbol || '$'}
                  </span>
                </div>
                <input type="number" name="payoutAmount" value={formData.payoutAmount} onChange={handleChange} className={`block w-full pl-7 pr-12 py-2 border ${errors.payoutAmount ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} placeholder="0.00" step="0.01" min="0" data-id="element-12214" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-12215">
                  <span className="text-gray-500 sm:text-sm" data-id="element-12216">
                    {formData.payoutModelId === 'revshare' ? '%' : ''}
                  </span>
                </div>
              </div>
              {errors.payoutAmount && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12217">
                  {errors.payoutAmount}
                </p>}
            </div>
            <div data-id="element-12218">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12219">
                Hold Days
              </label>
              <input type="number" name="holdDays" value={formData.holdDays} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" min="0" data-id="element-12220" />
            </div>
            <div data-id="element-12221">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12222">
                Payout Notes
              </label>
              <textarea name="payoutNotes" value={formData.payoutNotes} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., Weekly payment, NET7" data-id="element-12223" />
            </div>
          </div>;
      case 'targeting':
        return <div className="space-y-6" data-id="element-12224">
            <div data-id="element-12225">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12226">
                GEO Targeting*
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12227">
                <div data-id="element-12228">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12229">
                    Include GEOs
                  </h4>
                  <div className="h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12230">
                    {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12231">
                        <input type="checkbox" id={`geo-${geo.id}`} checked={formData.targeting.geos.includes(geo.id)} onChange={e => handleMultiSelect('targeting.geos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12232" />
                        <label htmlFor={`geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12233">
                          {geo.name} ({geo.code}) - {geo.tier}
                        </label>
                      </div>)}
                  </div>
                  {errors['targeting.geos'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12234">
                      {errors['targeting.geos']}
                    </p>}
                </div>
                <div data-id="element-12235">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12236">
                    Exclude GEOs
                  </h4>
                  <div className="h-60 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12237">
                    {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12238">
                        <input type="checkbox" id={`exclude-geo-${geo.id}`} checked={formData.targeting.excludedGeos.includes(geo.id)} onChange={e => handleMultiSelect('targeting.excludedGeos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12239" />
                        <label htmlFor={`exclude-geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12240">
                          {geo.name} ({geo.code})
                        </label>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-12241">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12242">
                Device Targeting*
              </label>
              <div className="flex flex-wrap gap-4" data-id="element-12243">
                {devices.map(device => <div key={device.id} className="flex items-center" data-id="element-12244">
                    <input type="checkbox" id={`device-${device.id}`} checked={formData.targeting.devices.includes(device.id)} onChange={e => handleMultiSelect('targeting.devices', device.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12245" />
                    <label htmlFor={`device-${device.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12246">
                      {device.name}
                    </label>
                  </div>)}
              </div>
              {errors['targeting.devices'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12247">
                  {errors['targeting.devices']}
                </p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12248">
              <div data-id="element-12249">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12250">
                  Operating Systems
                </label>
                <div className="h-40 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12251">
                  {operatingSystems.map(os => <div key={os.id} className="flex items-center mb-2" data-id="element-12252">
                      <input type="checkbox" id={`os-${os.id}`} checked={formData.targeting.operatingSystems.includes(os.id)} onChange={e => handleMultiSelect('targeting.operatingSystems', os.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12253" />
                      <label htmlFor={`os-${os.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12254">
                        {os.name}
                      </label>
                    </div>)}
                </div>
              </div>
              <div data-id="element-12255">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12256">
                  Browsers
                </label>
                <div className="h-40 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12257">
                  {browsers.map(browser => <div key={browser.id} className="flex items-center mb-2" data-id="element-12258">
                      <input type="checkbox" id={`browser-${browser.id}`} checked={formData.targeting.browsers.includes(browser.id)} onChange={e => handleMultiSelect('targeting.browsers', browser.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12259" />
                      <label htmlFor={`browser-${browser.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12260">
                        {browser.name}
                      </label>
                    </div>)}
                </div>
              </div>
            </div>
            <div data-id="element-12261">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12262">
                Languages
              </label>
              <div className="flex flex-wrap gap-4 border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12263">
                {languages.map(language => <div key={language.id} className="flex items-center" data-id="element-12264">
                    <input type="checkbox" id={`language-${language.id}`} checked={formData.targeting.languages.includes(language.id)} onChange={e => handleMultiSelect('targeting.languages', language.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12265" />
                    <label htmlFor={`language-${language.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12266">
                      {language.name}
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12267">
              <div data-id="element-12268">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12269">
                  Time of Day (24h format)
                </label>
                <div className="flex items-center space-x-4" data-id="element-12270">
                  <div data-id="element-12271">
                    <label htmlFor="timeStart" className="block text-xs text-gray-500 dark:text-gray-400" data-id="element-12272">
                      Start
                    </label>
                    <input type="number" id="timeStart" min="0" max="24" value={formData.targeting.timeOfDay.start} onChange={e => setFormData(prev => ({
                    ...prev,
                    targeting: {
                      ...prev.targeting,
                      timeOfDay: {
                        ...prev.targeting.timeOfDay,
                        start: parseInt(e.target.value)
                      }
                    }
                  }))} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-12273" />
                  </div>
                  <div data-id="element-12274">
                    <label htmlFor="timeEnd" className="block text-xs text-gray-500 dark:text-gray-400" data-id="element-12275">
                      End
                    </label>
                    <input type="number" id="timeEnd" min="0" max="24" value={formData.targeting.timeOfDay.end} onChange={e => setFormData(prev => ({
                    ...prev,
                    targeting: {
                      ...prev.targeting,
                      timeOfDay: {
                        ...prev.targeting.timeOfDay,
                        end: parseInt(e.target.value)
                      }
                    }
                  }))} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" data-id="element-12276" />
                  </div>
                </div>
              </div>
              <div data-id="element-12277">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12278">
                  Days of Week
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-12279">
                  {[{
                  id: 0,
                  name: 'Sunday'
                }, {
                  id: 1,
                  name: 'Monday'
                }, {
                  id: 2,
                  name: 'Tuesday'
                }, {
                  id: 3,
                  name: 'Wednesday'
                }, {
                  id: 4,
                  name: 'Thursday'
                }, {
                  id: 5,
                  name: 'Friday'
                }, {
                  id: 6,
                  name: 'Saturday'
                }].map(day => <div key={day.id} className="flex items-center" data-id="element-12280">
                      <input type="checkbox" id={`day-${day.id}`} checked={formData.targeting.daysOfWeek.includes(day.id)} onChange={e => handleMultiSelect('targeting.daysOfWeek', day.id.toString(), e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12281" />
                      <label htmlFor={`day-${day.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12282">
                        {day.name}
                      </label>
                    </div>)}
                </div>
              </div>
            </div>
          </div>;
      case 'segment':
        return <div className="space-y-6" data-id="element-12283">
            <div data-id="element-12284">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12285">
                Segment Targeting*
              </label>
              <div className="border border-gray-300 dark:border-gray-700 rounded-md p-4" data-id="element-12286">
                {segments.map(segment => <div key={segment.id} className="flex items-center mb-3" data-id="element-12287">
                    <input type="checkbox" id={`segment-${segment.id}`} checked={formData.segmentTargeting.segments.includes(segment.id)} onChange={e => handleMultiSelect('segmentTargeting.segments', segment.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12288" />
                    <label htmlFor={`segment-${segment.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12289">
                      <span className="font-medium" data-id="element-12290">{segment.name}</span> -{' '}
                      <span className="text-gray-600 dark:text-gray-400" data-id="element-12291">
                        {segment.description}
                      </span>
                    </label>
                  </div>)}
              </div>
              {errors['segmentTargeting.segments'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12292">
                  {errors['segmentTargeting.segments']}
                </p>}
            </div>
            <div data-id="element-12293">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12294">
                Fallback Strategy (when algorithm confidence {'<'} threshold)
              </label>
              <select name="segmentTargeting.fallbackType" value={formData.segmentTargeting.fallbackType} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12295">
                {algorithmTypes.map(type => <option key={type.id} value={type.id} data-id="element-12296">
                    {type.name} - {type.description}
                  </option>)}
              </select>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md" data-id="element-12297">
              <div className="flex items-start" data-id="element-12298">
                <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-12299" />
                <div data-id="element-12300">
                  <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300" data-id="element-12301">
                    Algorithm Confidence Fallback
                  </h4>
                  <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-400" data-id="element-12302">
                    If the algorithm's confidence score falls below the
                    threshold set in the Owner Control Layer, this fallback
                    strategy will be used to select the offer.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 'caps':
        return <div className="space-y-6" data-id="element-12303">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-12304">
              <div data-id="element-12305">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12306">
                  Daily Cap*
                </label>
                <input type="number" name="caps.daily" value={formData.caps.daily} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['caps.daily'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12307" />
                {errors['caps.daily'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12308">
                    {errors['caps.daily']}
                  </p>}
              </div>
              <div data-id="element-12309">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12310">
                  Hourly Cap*
                </label>
                <input type="number" name="caps.hourly" value={formData.caps.hourly} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['caps.hourly'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12311" />
                {errors['caps.hourly'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12312">
                    {errors['caps.hourly']}
                  </p>}
              </div>
            </div>
            <div data-id="element-12313">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12314">
                Frequency Cap per User*
              </label>
              <input type="number" name="caps.frequency" value={formData.caps.frequency} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors['caps.frequency'] ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12315" />
              {errors['caps.frequency'] && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12316">
                  {errors['caps.frequency']}
                </p>}
            </div>
            <div data-id="element-12317">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12318">
                Pacing
              </label>
              <select name="caps.pacingId" value={formData.caps.pacingId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12319">
                {pacingOptions.map(option => <option key={option.id} value={option.id} data-id="element-12320">
                    {option.name} - {option.description}
                  </option>)}
              </select>
            </div>
          </div>;
      case 'priority':
        return <div className="space-y-6" data-id="element-12321">
            <div data-id="element-12322">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12323">
                Priority*
              </label>
              <div className="flex items-center" data-id="element-12324">
                <input type="number" name="priority" value={formData.priority} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.priority ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12325" />
              </div>
              {errors.priority && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12326">
                  {errors.priority}
                </p>}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12327">
                Lower numbers mean higher priority. Priority 1 offers will be
                served before priority 2, etc.
              </p>
            </div>
            <div data-id="element-12328">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12329">
                Weight*
              </label>
              <div className="flex items-center" data-id="element-12330">
                <input type="number" name="weight" value={formData.weight} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.weight ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-700'} rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white`} min="1" data-id="element-12331" />
              </div>
              {errors.weight && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-12332">
                  {errors.weight}
                </p>}
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12333">
                Higher weights get more traffic in rotations. An offer with
                weight 100 gets twice as much traffic as an offer with weight
                50.
              </p>
            </div>
          </div>;
      case 'compliance':
        return <div className="space-y-6" data-id="element-12334">
            <div className="flex items-center" data-id="element-12335">
              <input type="checkbox" id="adultFlag" name="compliance.adultFlag" checked={formData.compliance.adultFlag} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12336" />
              <label htmlFor="adultFlag" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12337">
                Adult Content Flag
              </label>
            </div>
            <div data-id="element-12338">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-12339">
                Restricted GEOs
              </label>
              <div className="h-40 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-md p-3" data-id="element-12340">
                {geos.map(geo => <div key={geo.id} className="flex items-center mb-2" data-id="element-12341">
                    <input type="checkbox" id={`restricted-geo-${geo.id}`} checked={formData.compliance.restrictedGeos.includes(geo.id)} onChange={e => handleMultiSelect('compliance.restrictedGeos', geo.id, e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12342" />
                    <label htmlFor={`restricted-geo-${geo.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12343">
                      {geo.name} ({geo.code})
                    </label>
                  </div>)}
              </div>
            </div>
            <div className="flex items-center" data-id="element-12344">
              <input type="checkbox" id="ageGate" name="compliance.ageGate" checked={formData.compliance.ageGate} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12345" />
              <label htmlFor="ageGate" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12346">
                Age Gate Required
              </label>
            </div>
            <div className="flex items-center" data-id="element-12347">
              <input type="checkbox" id="kycNeeded" name="compliance.kycNeeded" checked={formData.compliance.kycNeeded} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12348" />
              <label htmlFor="kycNeeded" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12349">
                KYC Needed
              </label>
            </div>
            <div data-id="element-12350">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12351">
                Partner Terms URL
              </label>
              <input type="url" name="compliance.partnerTermsUrl" value={formData.compliance.partnerTermsUrl} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="https://example.com/terms" data-id="element-12352" />
            </div>
            <div data-id="element-12353">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12354">
                Internal Risk Score
              </label>
              <select name="compliance.internalRiskScore" value={formData.compliance.internalRiskScore} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12355">
                <option value={1} data-id="element-12356">1 - Low Risk</option>
                <option value={2} data-id="element-12357">2 - Medium-Low Risk</option>
                <option value={3} data-id="element-12358">3 - Medium Risk</option>
                <option value={4} data-id="element-12359">4 - Medium-High Risk</option>
                <option value={5} data-id="element-12360">5 - High Risk</option>
              </select>
            </div>
          </div>;
      case 'tracking':
        return <div className="space-y-6" data-id="element-12361">
            <div data-id="element-12362">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12363">
                Click Macro Tokens
              </label>
              <input type="text" name="tracking.clickMacroTokens" value={formData.tracking.clickMacroTokens} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="{click_id}" data-id="element-12364" />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12365">
                The token(s) that will be replaced with the actual click ID in
                tracking URLs.
              </p>
            </div>
            <div data-id="element-12366">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12367">
                S2S Postback URL
              </label>
              <input type="text" name="tracking.postbackUrl" value={formData.tracking.postbackUrl} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="https://sinshift.com/postback?click_id={click_id}&status={status}" data-id="element-12368" />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12369">
                URL that will be called when a conversion is registered.
              </p>
            </div>
            <div className="flex items-center" data-id="element-12370">
              <input type="checkbox" id="webhookEnabled" name="tracking.webhookEnabled" checked={formData.tracking.webhookEnabled} onChange={handleChange} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12371" />
              <label htmlFor="webhookEnabled" className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12372">
                Enable Webhook
              </label>
            </div>
            <div data-id="element-12373">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12374">
                Pixel Type
              </label>
              <select name="tracking.pixelTypeId" value={formData.tracking.pixelTypeId} onChange={handleChange} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12375">
                {pixelTypes.map(type => <option key={type.id} value={type.id} data-id="element-12376">
                    {type.name}
                  </option>)}
              </select>
            </div>
          </div>;
      case 'creatives':
        return <div className="space-y-6" data-id="element-12377">
            <div className="flex justify-between items-center" data-id="element-12378">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white" data-id="element-12379">
                Creatives
              </h3>
              <button type="button" onClick={handleAddCreative} className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center" data-id="element-12380">
                <Plus size={16} className="mr-2" data-id="element-12381" />
                Add Creative
              </button>
            </div>
            {formData.creatives.length === 0 ? <div className="text-center py-6 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-md" data-id="element-12382">
                <p className="text-gray-500 dark:text-gray-400" data-id="element-12383">
                  No creatives added yet. Click "Add Creative" to add one.
                </p>
              </div> : <div className="space-y-8" data-id="element-12384">
                {formData.creatives.map((creative, index) => <div key={creative.id} className="border border-gray-300 dark:border-gray-700 rounded-md p-4" data-id="element-12385">
                    <div className="flex justify-between items-center mb-4" data-id="element-12386">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white" data-id="element-12387">
                        Creative #{index + 1}
                      </h4>
                      <button type="button" onClick={() => handleRemoveCreative(index)} className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" data-id="element-12388">
                        <Trash size={16} data-id="element-12389" />
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-12390">
                      <div data-id="element-12391">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12392">
                          Type
                        </label>
                        <select value={creative.type} onChange={e => handleCreativeChange(index, 'type', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12393">
                          <option value="banner" data-id="element-12394">Banner</option>
                          <option value="video" data-id="element-12395">Video</option>
                          <option value="text" data-id="element-12396">Text</option>
                        </select>
                      </div>
                      <div data-id="element-12397">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12398">
                          Aspect Ratio
                        </label>
                        <select value={creative.aspectRatioId} onChange={e => handleCreativeChange(index, 'aspectRatioId', e.target.value)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-700 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md dark:bg-gray-800 dark:text-white" data-id="element-12399">
                          {aspectRatios.map(ratio => <option key={ratio.id} value={ratio.id} data-id="element-12400">
                              {ratio.name} ({ratio.width}:{ratio.height})
                            </option>)}
                        </select>
                      </div>
                    </div>
                    <div className="mb-4" data-id="element-12401">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12402">
                        URL
                      </label>
                      <input type="url" value={creative.url} onChange={e => handleCreativeChange(index, 'url', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="https://example.com/image.jpg" data-id="element-12403" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-12404">
                      <div data-id="element-12405">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12406">
                          Headline
                        </label>
                        <input type="text" value={creative.headline} onChange={e => handleCreativeChange(index, 'headline', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., Live Cams Now" data-id="element-12407" />
                      </div>
                      <div data-id="element-12408">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-12409">
                          CTA
                        </label>
                        <input type="text" value={creative.cta} onChange={e => handleCreativeChange(index, 'cta', e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm dark:bg-gray-800 dark:text-white" placeholder="e.g., Join Free" data-id="element-12410" />
                      </div>
                    </div>
                    <div className="flex items-center" data-id="element-12411">
                      <input type="checkbox" id={`approved-${index}`} checked={creative.approved} onChange={e => handleCreativeChange(index, 'approved', e.target.checked)} className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-700 rounded" data-id="element-12412" />
                      <label htmlFor={`approved-${index}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-100" data-id="element-12413">
                        Approved
                      </label>
                    </div>
                  </div>)}
              </div>}
          </div>;
      case 'qa':
        return <div className="space-y-6" data-id="element-12414">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5" data-id="element-12415">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4" data-id="element-12416">
                QA Tools
              </h3>
              <div className="space-y-4" data-id="element-12417">
                <div data-id="element-12418">
                  <button type="button" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" onClick={() => {
                  // Open the offer URL in a new tab
                  if (formData.baseUrl) {
                    window.open(formData.baseUrl, '_blank');
                  } else {
                    alert('Please enter a base URL first');
                  }
                }} data-id="element-12419">
                    Test Click
                  </button>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12420">
                    Opens the offer URL in a new tab to test the click.
                  </p>
                </div>
                <div data-id="element-12421">
                  <button type="button" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors" onClick={() => {
                  // Show preview in a modal
                  if (formData.baseUrl) {
                    alert('Preview would be shown in a modal');
                  } else {
                    alert('Please enter a base URL first');
                  }
                }} data-id="element-12422">
                    Preview Landing
                  </button>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-id="element-12423">
                    Shows a preview of the landing page in a modal.
                  </p>
                </div>
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-id="element-12424">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2" data-id="element-12425">
                    Link Validator Status
                  </h4>
                  <div className="flex items-center" data-id="element-12426">
                    <div className={`w-3 h-3 rounded-full mr-2 ${formData.baseUrl.startsWith('http') ? 'bg-green-500' : 'bg-red-500'}`} data-id="element-12427"></div>
                    <span className={`text-sm ${formData.baseUrl.startsWith('http') ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`} data-id="element-12428">
                      {formData.baseUrl.startsWith('http') ? 'URL format is valid' : 'URL must start with http:// or https://'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-12429">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-between items-center" data-id="element-12430">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-12431">
          {mode === 'create' ? 'Create Backoffer' : 'Edit Backoffer'}
        </h2>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" data-id="element-12432">
          <X size={20} data-id="element-12433" />
        </button>
      </div>
      <div className="p-6" data-id="element-12434">
        <div className="mb-6 overflow-x-auto" data-id="element-12435">
          <div className="flex space-x-2" data-id="element-12436">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 text-sm font-medium rounded-md ${activeTab === tab.id ? 'bg-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`} data-id="element-12437">
                {tab.label}
              </button>)}
          </div>
        </div>
        <form onSubmit={handleSubmit} data-id="element-12438">
          {renderTabContent()}
          <div className="mt-8 flex justify-end space-x-3" data-id="element-12439">
            <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12440">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500" data-id="element-12441">
              {mode === 'create' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};